import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Image,
  Link,
  VStack,
} from "@chakra-ui/react";
import { FC, useRef } from "react";
import useScreen from "../../hooks/useScreen";
import { Channel } from "../../interfaces/tracking";
import { Analytics, EventNames } from "../../utils/tracking";

interface ICustomModal {
  onClose?: () => void;
  onConfirm?: () => void;
  isOpen: boolean;
  title: string;
  body?: string;
  titleBtnConfirm?: string;
  titleBtnCancel?: string;
  showIcon?: boolean;
  screen?: string;
  href?: string;
  onClick?: () => void;
  isExternal?: boolean;
  isSuccess?: boolean;
}

export const CustomModal: FC<ICustomModal> = (props) => {
  const {
    onClose,
    onConfirm,
    isOpen,
    title,
    body,
    titleBtnConfirm,
    titleBtnCancel,
    showIcon,
    screen,
    href,
    onClick,
    isExternal,
    isSuccess,
  } = props;

  const cancelRef = useRef(null);
  const { isMobile } = useScreen();
  const urlIcon = isSuccess
    ? "/assets/images/burstCheckActive.svg"
    : "/assets/images/Exclamation-triangle.svg";

  const platform: string =
    new URLSearchParams(window.location.search).get("platform") ?? "";

  const onConfirmHandler = () => {
    Analytics.event(Channel.GA, EventNames.SAVE_PHOTO_ACCEPTED, {
      device: isMobile ? "mobile" : "desktop",
      option: screen,
    });
    onConfirm && onConfirm();
  };

  const onCloseHandler = () => {
    Analytics.event(Channel.GA, EventNames.SAVE_PHOTO_ACCEPTED, {
      device: isMobile ? "mobile" : "desktop",
      option: screen,
    });
    onClose && onClose();
  };

  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={
        platform.includes("g-mob") && onClose ? onClose : onConfirmHandler
      }
      isOpen={isOpen}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <AlertDialogOverlay />

      <AlertDialogContent>
        <VStack justifyContent={{ base: "center", md: "center" }}>
          {showIcon && (
            <Image
              mt={{ base: "24px", md: "34px" }}
              mb={{ base: "10px", md: "14px" }}
              src={process.env.PUBLIC_URL + urlIcon}
              width={{ base: "53px", md: "58px" }}
              height={{ base: "53px", md: "58px" }}
            />
          )}
          <AlertDialogHeader
            className={showIcon ? "custom-modal" : ""}
            fontWeight={{ base: "500", md: "500" }}
            fontSize={
              platform.includes("g-mob")
                ? { base: "16px", md: "18px" }
                : { base: "24px" }
            }
            letterSpacing={{ base: "0.15px", md: "0.36px" }}
            lineHeight={{ base: "28px" }}
            pb="0"
            paddingTop={"0px !important"}
            justifyContent={{ base: "center", md: "center" }}
            textAlign={{ base: "center", md: "center" }}
            color="#000"
          >
            {title}
          </AlertDialogHeader>

          <AlertDialogCloseButton style={{ color: "#000" }} onClick={onClose} />
          {body && (
            <AlertDialogBody
              fontSize={{ base: "12px", md: "12px" }}
              fontWeight={{ base: "450" }}
              lineHeight={{ base: "16px" }}
              textAlign={{ base: "center", md: "center" }}
              justifyContent={{ base: "center", md: "center" }}
              px={{ base: "24px", md: "34px" }}
              pt={platform.includes("g-mob") ? "0px" : "auto"}
              color="#000"
            >
              {body}
            </AlertDialogBody>
          )}
          <VStack
            justifyContent="center"
            pb="20px"
            flexDirection="row"
            pt="0"
            gap={{ base: "14px", md: "20px" }}
            mt={
              platform.includes("g-mob") ? "0px" : { base: "30px", md: "34px" }
            }
          >
            {onConfirm && (
              <Button
                fontSize={{ base: "14px" }}
                border="1px solid #000000"
                _hover={{ bg: "#FFF" }}
                bg="transparent"
                px={{ base: "24px", md: "34px" }}
                py={{ base: "10px", md: "15px" }}
                onClick={onConfirmHandler}
                color="#000000"
              >
                {titleBtnConfirm ?? "Yes"}
              </Button>
            )}

            {!href && onClose && !isSuccess ? (
              <Button
                ref={cancelRef}
                onClick={platform.includes("g-mob") ? onClose : onCloseHandler}
                className="text-transform-normal"
                fontSize={{ base: "14px" }}
                letterSpacing={{ base: "0.25px", md: "0.35px" }}
                lineHeight={{ base: "20px", md: "28.5px" }}
                h="max-content"
                w="max-content"
                px="0"
                pb={{ base: "3px" }}
                borderBottom={{
                  base: "1px solid #000000",
                  md: "1.5px solid #000000",
                }}
                _hover={{ bg: "transparante" }}
                color="#000000"
                bg="transparent"
              >
                {titleBtnCancel ?? "No, thanks!"}
              </Button>
            ) : (
              <Button
                fontSize={{ base: "14px" }}
                border="1px solid #fff"
                ref={cancelRef}
                onClick={platform.includes("g-mob") ? onClose : onCloseHandler}
                _hover={{ bg: "#000000" }}
                bg="#000000"
                height={platform.includes("g-mob") ? "40px" : "auto"}
                width={platform.includes("g-mob") ? "100px" : "auto"}
                px={{ base: "24px", md: "34px" }}
                py={{ base: "10px", md: "15px" }}
                color="#fff"
              >
                {titleBtnCancel ?? "Yes"}
              </Button>
            )}
            {href && !onConfirm && (
              <Link href={href} onClick={onClick} isExternal={isExternal}>
                <Button
                  fontSize={{ base: "14px" }}
                  border="1px solid #000000"
                  _hover={{ bg: "#FFF" }}
                  bg="transparent"
                  height={platform.includes("g-mob") ? "40px" : "auto"}
                  width={platform.includes("g-mob") ? "100px" : "auto"}
                  px={{ base: "24px", md: "34px" }}
                  py={{ base: "10px", md: "15px" }}
                  color="#000000"
                >
                  {titleBtnConfirm ?? "Yes"}
                </Button>
              </Link>
            )}
          </VStack>
        </VStack>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CustomModal;
